import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Clock, UtensilsCrossed, Grid3X3, DollarSign, Users, TrendingUp, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TipsChart from '../../components/TipsChart';
import TonightSalesTile from '../../components/TonightSalesTile';

function StatCard({ label, value, icon: Icon, accent, onClick }) {
  return (
    <div onClick={onClick} className={`bg-white/[0.03] border border-white/5 rounded-sm p-4 card-hover ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${accent === 'gold' ? 'bg-gold/10' : accent === 'blue' ? 'bg-blue-accent/10' : accent === 'green' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
          <Icon className={`w-5 h-5 ${accent === 'gold' ? 'text-gold' : accent === 'blue' ? 'text-blue-accent' : accent === 'green' ? 'text-emerald-400' : 'text-amber-400'}`} />
        </div>
        <div>
          <p className="text-[11px] text-zinc-500 font-sans uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-sans font-semibold text-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const [stats, setStats] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  // Bartender shares Live Orders + Tables with manager but doesn't have access
  // to menu / analytics / inventory / staff. Hide the cards (and skip the
  // admin-only stats fetch entirely) so they don't get console 403 spam or
  // dead-end clicks.
  const isAdminRole = !!user && ['manager', 'director', 'admin', 'owner'].includes(user.role);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api.get('/stats/dashboard'); setStats(data); } catch (err) { console.error('[OverviewPage.js] error:', err); }
  }, []);

  useEffect(() => {
    if (!isAdminRole) return undefined;
    fetchStats();
    // Tight 5s poll (was 10s) so the Active Orders KPI moves in lockstep
    // with the OrdersPage list and doesn't drift after a void or cash-OK.
    const i = setInterval(fetchStats, 5000);
    // Also re-fetch on tab focus / visibility / network change. PWA users
    // backgrounded the tab during prep service nights and came back to
    // stale numbers — these listeners snap the dashboard fresh on the
    // first interaction.
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchStats(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', fetchStats);
    window.addEventListener('online', fetchStats);
    return () => {
      clearInterval(i);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', fetchStats);
      window.removeEventListener('online', fetchStats);
    };
  }, [fetchStats, isAdminRole]);

  // Quick-link tiles are role-filtered so bartenders / waiters never see a
  // tile that 403s on click.
  const quickLinks = [
    { label: 'View Orders', path: '/dashboard/orders', icon: ShoppingCart, desc: 'Manage live orders', allowedRoles: ['manager', 'director', 'admin', 'owner', 'bartender'] },
    { label: 'Tables', path: '/dashboard/tables', icon: Grid3X3, desc: 'Floor map + clear', allowedRoles: ['manager', 'director', 'admin', 'owner', 'bartender'] },
    { label: 'Menu', path: '/dashboard/menu', icon: UtensilsCrossed, desc: 'Edit menu items', allowedRoles: ['manager', 'director', 'admin', 'owner'] },
    { label: 'Analytics', path: '/dashboard/analytics', icon: TrendingUp, desc: 'Revenue & timing', allowedRoles: ['manager', 'director', 'admin', 'owner'] },
    { label: 'Inventory', path: '/dashboard/inventory', icon: Clock, desc: 'Stock levels', allowedRoles: ['manager', 'director', 'admin', 'owner'] },
  ].filter(l => !user || l.allowedRoles.includes(user.role));

  return (
    <div className="space-y-6 animate-fadeIn">
      {isAdminRole && (
        <TonightSalesTile stats={stats} />
      )}

      {isAdminRole && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard label="Active Orders" value={stats.active_orders || 0} icon={ShoppingCart} accent="gold" onClick={() => navigate('/dashboard/orders')} />
          <StatCard label="Completed" value={stats.completed_today || 0} icon={UtensilsCrossed} accent="green" />
          {/* "Pending Cash" tile removed Feb 2026 — the underlying flow
              (customer picks "Pay Cash At Bar" → bartender approves at
              till) was retired by the owner. The backend status string
              `pending_cash` is intentionally LEFT in place so any
              historical orders carrying it on production don't crash. */}
          <StatCard label="Tables" value={`${stats.occupied_tables || 0}/${stats.total_tables || 0}`} icon={Grid3X3} accent="blue" onClick={() => navigate('/dashboard/tables')} />
          <StatCard label="Tonight" value={`$${(stats.today_revenue || 0).toFixed(0)}`} icon={DollarSign} accent="gold" onClick={() => navigate('/dashboard/analytics')} />
          <StatCard label="Staff" value={<span className="text-emerald-400">Online</span>} icon={Users} accent="green" onClick={() => navigate('/dashboard/staff')} />
        </div>
      )}

      {/* Tips Chart (Director / Manager only — auto-hidden for other roles) */}
      {isAdminRole && <TipsChart />}

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {quickLinks.map(link => (
          <div key={link.path} onClick={() => navigate(link.path)} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm card-hover cursor-pointer" data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
            <link.icon className="w-5 h-5 text-gold mb-2" />
            <p className="text-white font-sans text-sm font-medium">{link.label}</p>
            <p className="text-zinc-500 font-sans text-xs">{link.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
